import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import Topbar from "./Topbar";

const clamp = (n: number, lo: number, hi: number) => {
    return Math.max(lo, Math.min(hi, n));
};

const isEditableTarget = (el: Element | null): boolean => {
    if (!el) return false;
    const tag = (el as HTMLElement).tagName;
    const editableTags = ["INPUT", "TEXTAREA", "SELECT"];
    if (editableTags.includes(tag)) return true;
    const contentEditable = (el as HTMLElement).closest("[contenteditable=''], [contenteditable='true']");
    return !!contentEditable;
};

const findScrollableAncestor = (node: Element | null, stopAt: Element | null): HTMLElement | null => {
    let el: Element | null = node as Element | null;
    while (el && el !== stopAt && el instanceof HTMLElement) {
        if (el.dataset && (el.dataset.sectionScroll === "ignore" || el.dataset.scrollLock === "false")) return el;

        const style = getComputedStyle(el);
        const overflowY = style.overflowY;
        const canScroll = (overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight;

        if (canScroll) return el;
        el = el.parentElement;
    }
    return null;
};

const canVerticallyScroll = (el: HTMLElement, deltaY: number): boolean => {
    if (deltaY > 0) {
        return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    } else if (deltaY < 0) {
        return el.scrollTop > 0;
    }
    return false;
};

export interface ISectionSpec {
    id: string;
    content: React.ReactNode;
    bg?: string;
}

interface IFullPageScrollerProps {
    sections: ISectionSpec[];
    initialIndex?: number;
}

const FullPageScroller: React.FC<IFullPageScrollerProps> = ({ sections, initialIndex = 0 }) => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [active, setActive] = useState(initialIndex);
    const isAnimatingRef = useRef(false);
    const lastWheelTs = useRef(0);

    itemRefs.current = useMemo(() => Array.from({ length: sections.length }, () => null), [sections.length]);

    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    const scrollToIndex = useCallback((idx: number, cb?: () => void) => {
        const el = itemRefs.current[idx];
        if (!el || !containerRef.current) return;

        el.scrollIntoView({ behavior: "smooth", block: "start" });

        const t = setTimeout(() => {
            setActive(idx);
            cb?.();
            clearTimeout(t);
        }, 420);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const top = entries.reduce<{ ratio: number; idx: number } | null>((acc, e) => {
                    const idx = Number((e.target as HTMLElement).dataset.index);
                    if (!acc || e.intersectionRatio > acc.ratio) return { ratio: e.intersectionRatio, idx };
                    return acc;
                }, null);
                if (top && top.idx !== active) setActive(top.idx);
            },
            { root: containerRef.current, threshold: [0.5, 0.75, 0.99] }
        );

        itemRefs.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [active, sections.length]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onWheel = (e: WheelEvent) => {
            if (isEditableTarget(e.target as Element)) return;

            const scrollable = findScrollableAncestor(e.target as Element, containerRef.current);
            if (scrollable && canVerticallyScroll(scrollable, e.deltaY)) {
                return;
            }

            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

            e.preventDefault();

            const now = performance.now();
            if (isAnimatingRef.current || now - lastWheelTs.current < 300) return;
            lastWheelTs.current = now;

            const dir = e.deltaY > 0 ? 1 : -1;
            const next = clamp(active + dir, 0, sections.length - 1);
            if (next === active) return;
            isAnimatingRef.current = true;
            scrollToIndex(next, () => (isAnimatingRef.current = false));
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [active, isXs, scrollToIndex, sections.length]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const keysDown = ["ArrowDown", "PageDown"];
            const keysUp = ["ArrowUp", "PageUp"];
            const atTop = active === 0;
            const atBottom = active === sections.length - 1;

            if (keysDown.includes(e.key) && !atBottom) {
                e.preventDefault();
                scrollToIndex(active + 1);
            } else if (keysUp.includes(e.key) && !atTop) {
                e.preventDefault();
                scrollToIndex(active - 1);
            } else if (e.key === "Home") {
                e.preventDefault();
                scrollToIndex(0);
            } else if (e.key === "End") {
                e.preventDefault();
                scrollToIndex(sections.length - 1);
            }
        };

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, scrollToIndex, sections.length]);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: "relative",
                height: "100dvh",
                overflowY: "auto",
                scrollSnapType: "y mandatory",
                scrollBehavior: "smooth",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                    display: "none",
                },
            }}
        >
            {sections.map((section, index) => (
                <Box
                    key={section.id}
                    data-index={index}
                    ref={(el: HTMLDivElement | null) => {
                        itemRefs.current[index] = el;
                    }}
                    role="region"
                    aria-labelledby={`${section.id}-heading`}
                    sx={{
                        minHeight: "100dvh",
                        scrollSnapAlign: "start",
                        scrollSnapStop: "always",
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        background: section.bg ?? theme.palette.background.default,
                    }}
                >
                    {index === 0 && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                zIndex: 5,
                            }}
                        >
                            <Topbar />
                        </Box>
                    )}

                    <Box>{section.content}</Box>
                </Box>
            ))}
        </Box>
    );
};

export default memo(FullPageScroller);
